import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

export type DictationRecoveryState = 'recording' | 'finalizing' | 'failed'

export type DictationRecoveryItem = {
  id: string
  chat_id: string
  topic_id: string | null
  draft_key: string
  base_draft: string
  body: string
  mime_type: string
  filename: string
  audio: Blob | null
  created_at: number
  updated_at: number
  state: DictationRecoveryState
  reason: string | null
}

interface DictationRecoverySchema extends DBSchema {
  items: {
    key: string
    value: DictationRecoveryItem
    indexes: { by_updated: number; by_chat: string }
  }
}

const DB_NAME = 'agentchat-dictation-recovery'
const DB_VERSION = 1
const STORE_NAME = 'items'
const MAX_RECOVERY_ITEMS = 10

let _dbPromise: Promise<IDBPDatabase<DictationRecoverySchema>> | null = null

function db(): Promise<IDBPDatabase<DictationRecoverySchema>> {
  if (_dbPromise) return _dbPromise
  _dbPromise = openDB<DictationRecoverySchema>(DB_NAME, DB_VERSION, {
    upgrade(d: IDBPDatabase<DictationRecoverySchema>) {
      const store = d.createObjectStore(STORE_NAME, { keyPath: 'id' })
      store.createIndex('by_updated', 'updated_at')
      store.createIndex('by_chat', 'chat_id')
    },
  })
  return _dbPromise
}

async function pruneOldRecoveryItems(d: IDBPDatabase<DictationRecoverySchema>): Promise<void> {
  const items = await d.getAllFromIndex(STORE_NAME, 'by_updated')
  const stale = items.slice(0, Math.max(0, items.length - MAX_RECOVERY_ITEMS))
  await Promise.all(stale.map((item) => d.delete(STORE_NAME, item.id)))
}

export async function saveDictationRecovery(item: DictationRecoveryItem): Promise<void> {
  const d = await db()
  await d.put(STORE_NAME, item)
  await pruneOldRecoveryItems(d)
}

export async function deleteDictationRecovery(id: string): Promise<void> {
  const d = await db()
  await d.delete(STORE_NAME, id)
}

export async function listDictationRecoveriesForDraft(
  draftKey: string,
): Promise<DictationRecoveryItem[]> {
  const d = await db()
  const items = await d.getAll(STORE_NAME)
  return items
    .filter((item) => item.draft_key === draftKey)
    .sort((a, b) => b.updated_at - a.updated_at)
}
